import React, { useEffect, useRef, useState } from "react";
import { useApp } from "../context/AppContext";
import { Sparkles, X, Send, Plus, History, Loader2, RefreshCw, Wrench, RotateCcw } from "lucide-react";
import { safeBottom } from "../lib/pwa";
import { useFloatingFab, panelStyleFor, FAB_SIZE } from "../lib/useFloatingFab";

const CHIPS = {
  zh: {
    common: ["今天我要做什么？", "我有哪些未读提醒？"],
    pm: ["哪些项目逾期了？", "材料到货情况如何？", "帮我给业主写一条进度短信"],
    admin: ["哪些项目逾期了？", "本周待收款有多少？", "谁的证书快到期了？"],
    ceo: ["哪些项目逾期了？", "本周待收款有多少？", "线索跟进情况如何？"],
    eng_manager: ["哪些项目逾期了？", "材料到货情况如何？", "帮我给业主写一条进度短信"],
    sales: ["我的线索跟进情况？", "帮我查一个项目进度"],
    finance: ["本周待收款有多少？", "帮我查一个项目付款情况"],
    hr: ["谁的证书快到期了？", "有待审批的请假吗？"],
    default: ["我的打卡记录", "帮我创建一条明天的提醒"],
  },
  en: {
    common: ["What should I do today?", "Any unread alerts?"],
    pm: ["Which projects are overdue?", "Material delivery status?", "Draft a progress SMS to the owner"],
    admin: ["Which projects are overdue?", "Pending collections this week?", "Whose credentials expire soon?"],
    ceo: ["Which projects are overdue?", "Pending collections this week?", "How are my leads doing?"],
    eng_manager: ["Which projects are overdue?", "Material delivery status?", "Draft a progress SMS to the owner"],
    sales: ["How are my leads doing?", "Check a project status"],
    finance: ["Pending collections this week?", "Check a project's payments"],
    hr: ["Whose credentials expire soon?", "Any pending leave requests?"],
    default: ["My clock-in records", "Create a reminder for tomorrow"],
  },
};

export const AssistantWidget = () => {
  const { api, user, lang } = useApp();
  const zh = lang === "zh";
  const T = (z, e) => (zh ? z : e);
  const [open, setOpen] = useState(false);
  const [brief, setBrief] = useState(null);
  const [briefBusy, setBriefBusy] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [sid, setSid] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [showHist, setShowHist] = useState(false);
  const [sessions, setSessions] = useState([]);
  const bottomRef = useRef(null);
  const fab = useFloatingFab("assistant", { defaultPos: () => ({ x: window.innerWidth - FAB_SIZE * 2 - 40, y: window.innerHeight - safeBottom() - FAB_SIZE - 24 }), onTap: () => setOpen((o) => !o), panelOpen: open, avoid: ["softphone"] });
  const panelW = Math.min(400, Math.round(window.innerWidth * 0.94));
  const panelStyle = { ...panelStyleFor(fab.fabPos, panelW, Math.min(600, Math.round(window.innerHeight * 0.85))), width: panelW };
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);
  const loadBrief = async (refresh = false) => {
    setBriefBusy(true);
    try { const { data } = await api.get(`/assistant/briefing?lang=${lang}${refresh ? "&refresh=1" : ""}`); setBrief(data); }
    catch { setBrief(null); }
    setBriefBusy(false);
  };
  useEffect(() => { if (open && !brief) loadBrief(); }, [open]); // eslint-disable-line
  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: msg }]);
    setBusy(true);
    try {
      const { data } = await api.post("/assistant/chat", { sessionId: sid, message: msg, lang });
      setSid(data.sessionId);
      setMsgs((m) => [...m, { role: "assistant", content: data.reply, tool: data.tool, actionNote: data.actionNote }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", content: e?.detailText || T("出错了，请稍后重试", "Something went wrong, please retry") }]);
    }
    setBusy(false);
  };
  const newChat = () => { setSid(null); setMsgs([]); setShowHist(false); };
  const openHist = async () => {
    setShowHist(!showHist);
    if (!showHist) { try { const { data } = await api.get("/assistant/sessions"); setSessions(data); } catch {} }
  };
  const loadSession = async (s) => {
    try {
      const { data } = await api.get(`/assistant/sessions/${s.id}/messages`);
      setSid(s.id); setMsgs(data); setShowHist(false);
    } catch {}
  };
  const roleChips = [...(CHIPS[zh ? "zh" : "en"].common), ...((CHIPS[zh ? "zh" : "en"][user?.role]) || CHIPS[zh ? "zh" : "en"].default)];
  if (!user) return null;
  return (
    <>
      {fab.fabVisible && (
        <div className="fixed z-[9999] transition-[left,top] duration-200" style={{ left: fab.fabPos.x, top: fab.fabPos.y }} onMouseEnter={fab.cancelRetract} onMouseLeave={() => { if (fab.dock && !open) fab.retractSoon(); }}>
          <button data-testid="assistant-fab" onPointerDown={fab.onPointerDown}
            title={T("AI 工作助理（可拖动 · 拖到边缘贴边隐藏）", "AI assistant (drag to move · drop at the edge to dock)")}
            className="h-14 w-14 rounded-full bg-brand-grad text-white shadow-brand flex items-center justify-center hover:scale-105 transition-transform touch-none select-none cursor-grab active:cursor-grabbing">
            <Sparkles className="h-6 w-6 pointer-events-none" />
          </button>
          {!fab.dock && <button data-testid="assistant-hide" title={T("贴边隐藏（鼠标移到边缘再弹出）", "Dock to edge (hover the edge to show)")}
            onPointerDown={(e) => e.stopPropagation()} onClick={() => { setOpen(false); fab.hide(); }}
            className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-white text-slate-500 border border-slate-200 shadow flex items-center justify-center hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors">
            <X className="h-3 w-3" />
          </button>}
        </div>
      )}
      {fab.tabVisible && (
        <button data-testid="assistant-restore" data-side={fab.dock} onMouseEnter={() => fab.showPeek()} onClick={() => fab.showPeek(true)}
          title={T("AI 助理（移到此处或点击弹出）", "AI assistant (hover or tap to show)")} style={{ top: fab.tabTop }}
          className={`fixed z-[9999] h-12 w-5 bg-brand-grad text-white/90 shadow-brand flex items-center justify-center opacity-70 hover:opacity-100 hover:w-7 transition-[width,opacity] ${fab.dock === "left" ? "left-0 rounded-r-lg" : "right-0 rounded-l-lg"}`}>
          <Sparkles className="h-3.5 w-3.5" />
        </button>
      )}
      {open && fab.fabPos && (
        <div data-testid="assistant-panel" style={panelStyle} className="fixed z-[9999] h-[600px] max-h-[85vh] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden">
          <div className="bg-brand-grad text-white px-4 py-3 flex items-center gap-2 shrink-0">
            <Sparkles className="h-4 w-4 text-white" />
            <div className="min-w-0">
              <p className="text-sm font-bold leading-tight">{T("AI 工作助理", "AI Work Assistant")}</p>
              <p className="text-[10px] text-slate-400">{user.name || user.username} · {T("专属助理 · 数据实时", "personal · live data")}</p>
            </div>
            <div className="ml-auto flex items-center gap-1">
              <button data-testid="assistant-new-chat" onClick={newChat} title={T("新对话", "New chat")} className="p-1.5 rounded-lg hover:bg-slate-700"><Plus className="h-4 w-4" /></button>
              <button data-testid="assistant-history" onClick={openHist} title={T("历史会话", "History")} className="p-1.5 rounded-lg hover:bg-slate-700"><History className="h-4 w-4" /></button>
              <button data-testid="assistant-reset-pos" onClick={fab.reset} title={T("重置悬浮球位置（回到右下角并取消贴边）", "Reset button position (bottom-right, undock)")} className="p-1.5 rounded-lg hover:bg-slate-700"><RotateCcw className="h-4 w-4" /></button>
              <button data-testid="assistant-close" onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:bg-slate-700"><X className="h-4 w-4" /></button>
            </div>
          </div>
          {showHist && (
            <div className="border-b border-slate-100 max-h-40 overflow-y-auto shrink-0" data-testid="assistant-session-list">
              {sessions.length === 0 ? <p className="text-[11px] text-slate-300 text-center py-3">{T("暂无历史会话", "No past chats")}</p> :
                sessions.map((s) => (
                  <button key={s.id} data-testid={`assistant-session-${s.id}`} onClick={() => loadSession(s)}
                    className="w-full text-left px-4 py-2 text-xs text-slate-600 hover:bg-slate-50 border-b border-slate-50 truncate">
                    {s.title || T("对话", "Chat")}<span className="text-[9px] text-slate-300 ml-1.5">{String(s.updatedAt || "").slice(5, 16).replace("T", " ")}</span>
                  </button>
                ))}
            </div>
          )}
          <div className="flex-1 overflow-y-auto px-3.5 py-3 space-y-3 bg-slate-50">
            <div className="rounded-xl bg-white border border-amber-200 p-3" data-testid="assistant-briefing">
              <div className="flex items-center gap-1.5 mb-1">
                <p className="text-[11px] font-bold text-amber-600">☀️ {T("今日简报", "Today's Briefing")}</p>
                <button data-testid="assistant-briefing-refresh" onClick={() => loadBrief(true)} disabled={briefBusy}
                  className="ml-auto text-slate-300 hover:text-slate-500 disabled:opacity-40"><RefreshCw className={`h-3 w-3 ${briefBusy ? "animate-spin" : ""}`} /></button>
              </div>
              {briefBusy && !brief ? <p className="text-[11px] text-slate-300">{T("正在为您生成…", "Generating…")}</p>
                : brief ? <p className="text-[11px] text-slate-600 whitespace-pre-wrap leading-relaxed">{brief.text}</p>
                  : <p className="text-[11px] text-slate-300">{T("简报暂不可用", "Briefing unavailable")}</p>}
            </div>
            {msgs.length === 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="assistant-chips">
                {roleChips.map((c, i) => (
                  <button key={i} data-testid={`assistant-chip-${i}`} onClick={() => send(c)}
                    className="text-[11px] px-2.5 py-1.5 rounded-full bg-white border border-slate-200 text-slate-600 hover:border-brand-300 hover:text-brand-600">{c}</button>
                ))}
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-xs leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bubble-mine" : "bg-white border border-slate-200 text-slate-700"}`}
                  data-testid={`assistant-msg-${i}`}>
                  {m.content}
                  {m.tool && <p className="mt-1 text-[9px] text-slate-400 flex items-center gap-1"><Wrench className="h-2.5 w-2.5" />{m.tool}</p>}
                  {m.actionNote && <p className="mt-1 text-[10px] font-semibold text-emerald-600" data-testid={`assistant-action-${i}`}>✓ {m.actionNote}</p>}
                </div>
              </div>
            ))}
            {busy && <div className="flex justify-start"><div className="rounded-2xl px-3 py-2 bg-white border border-slate-200"><Loader2 className="h-4 w-4 animate-spin text-slate-300" /></div></div>}
            <div ref={bottomRef} />
          </div>
          <div className="p-3 border-t border-slate-100 bg-white shrink-0">
            <div className="flex gap-2">
              <input data-testid="assistant-input" value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
                placeholder={T("问我任何工作问题，或让我帮你起草/提醒…", "Ask anything, or have me draft & remind…")}
                className="flex-1 text-xs rounded-xl border border-slate-200 px-3 py-2.5 focus:outline-none focus:border-amber-400" />
              <button data-testid="assistant-send" disabled={busy || !input.trim()} onClick={() => send()}
                className="rounded-xl bg-brand-grad text-white px-3.5 disabled:opacity-40 hover:opacity-90"><Send className="h-4 w-4" /></button>
            </div>
            <p className="text-[9px] text-slate-300 mt-1.5 text-center">{T("AI 起草的短信/邮件仅进草稿箱，人工确认后才发送 · 工程推进需本人操作", "AI drafts go to the draft box (human-confirmed) · phase advances stay manual")}</p>
          </div>
        </div>
      )}
    </>
  );
};
